import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { styles } from "../styles";

export default function OwnerPanelPage() {
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState("mechanical");
  const [adminEmail, setAdminEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState("");
  const [createError, setCreateError] = useState("");
  const [passwordResetLink, setPasswordResetLink] = useState("");

  // Per-org lock + delete actions. Lock is a single confirm (low-stakes,
  // reversible). Delete is a DOUBLE confirm because it hides the org +
  // all its data from the live UI. Deleted orgs aren't truly wiped — the
  // softDeleteOrg function just flags the doc, so we can restore later
  // if it was a mistake.
  const [orgBusy, setOrgBusy] = useState(null); // orgId currently being mutated
  const [orgMsg, setOrgMsg] = useState("");
  const toggleOrgLock = async (org) => {
    const next = !org.locked;
    const verb = next ? "lock" : "unlock";
    if (!window.confirm(`${next ? "Lock" : "Unlock"} "${org.name}"?`)) return;
    setOrgBusy(org.id);
    setOrgMsg("");
    try {
      const fn = httpsCallable(functions, "setOrgLocked");
      const res = await fn({ orgId: org.id, locked: next });
      setOrgMsg(res.data?.message || `Org ${verb}ed.`);
    } catch (err) {
      setOrgMsg(`Failed to ${verb}: ${err.message}`);
    } finally {
      setOrgBusy(null);
    }
  };
  // HARD delete — irreversible nuke. Lock is the "soft delete" equivalent
  // (data preserved, can be re-enabled), so this is for the rare case
  // where the user actually wants to wipe an org entirely. Two confirm
  // dialogs because there's no undo.
  const hardDelete = async (org) => {
    const first = window.confirm(
      `DELETE "${org.name}" entirely?\n\nThis WIPES the org plus EVERY customer, building, equipment record, service ticket, quote, PO, message thread, user account, and Auth login that belongs to it. Cannot be undone.`
    );
    if (!first) return;
    const second = window.confirm(
      `Really delete "${org.name}"?\n\nThis is final. Type-check: org id is "${org.id}".\n\nClick OK to permanently destroy all data, Cancel to back out.`
    );
    if (!second) return;
    setOrgBusy(org.id);
    setOrgMsg("");
    try {
      const fn = httpsCallable(functions, "hardDeleteOrg");
      const res = await fn({ orgId: org.id });
      setOrgMsg(res.data?.message || "Deleted.");
    } catch (err) {
      setOrgMsg(`Failed to delete: ${err.message}`);
    } finally {
      setOrgBusy(null);
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, "organizations"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setOrgs(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOrgsLoading(false);
      },
      (error) => {
        console.error("Error listing organizations", error);
        setOrgsLoading(false);
      }
    );
    return unsub;
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    setCreateMessage("");
    setCreateError("");
    setPasswordResetLink("");

    try {
      const createOrg = httpsCallable(functions, "createOrganization");
      const result = await createOrg({ orgName, orgType, adminEmail });

      setCreateMessage(result.data?.message || "Organization created.");
      setPasswordResetLink(result.data?.passwordResetLink || "");
      setOrgName("");
      setAdminEmail("");
      setOrgType("mechanical");
    } catch (err) {
      setCreateError(err.message || "Failed to create organization.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <h2>Owner Console</h2>
      <p style={{ color: "#666" }}>
        You're signed in as the project owner. From here you create new
        organizations and provision their first Admin user.
      </p>

      <h3>Create New Organization</h3>
      <div style={styles.card}>
        <input
          style={styles.input}
          placeholder="Organization Name (e.g. Acme Mechanical)"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
        />
        <br />
        <label>Type: </label>
        <select value={orgType} onChange={(e) => setOrgType(e.target.value)}>
          <option value="mechanical">Mechanical Company</option>
          <option value="facility">Facility Customer</option>
        </select>
        <br />
        <input
          style={styles.input}
          type="email"
          placeholder="Admin Email (this person becomes the org's first Admin)"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
        />
        <br />
        <button
          style={styles.button}
          onClick={handleCreate}
          disabled={creating || !orgName || !adminEmail}
        >
          {creating ? "Creating..." : "Create Organization"}
        </button>

        {createMessage && (
          <p style={{ color: "green", marginTop: "8px" }}>{createMessage}</p>
        )}
        {createError && (
          <p style={{ color: "red", marginTop: "8px" }}>{createError}</p>
        )}

        {passwordResetLink && (
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              background: "#f0f8ff",
              border: "1px solid #4a90e2",
              borderRadius: "6px",
            }}
          >
            <strong>Password Reset Link (share with new Admin):</strong>
            <br />
            <input
              style={{ ...styles.input, fontSize: "11px", maxWidth: "100%" }}
              readOnly
              value={passwordResetLink}
              onFocus={(e) => e.target.select()}
            />
            <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
              Copy this link and send it to the new Admin. When they click it,
              they will set their password and can then log in.
            </p>
          </div>
        )}
      </div>

      <h3>Existing Organizations</h3>
      {orgMsg && (
        <p style={{ fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
          {orgMsg}
        </p>
      )}
      {orgsLoading ? (
        <p>Loading organizations...</p>
      ) : orgs.length === 0 ? (
        <p>No organizations yet. Create one above.</p>
      ) : (
        orgs.map((org) => (
          <div key={org.id} style={styles.card}>
            <strong>{org.name}</strong>
            {org.locked && (
              <span
                style={{
                  marginLeft: "8px",
                  background: "#fff3cd",
                  color: "#9a6b00",
                  fontSize: "11px",
                  padding: "1px 6px",
                  borderRadius: "8px",
                  fontWeight: 700,
                }}
              >
                LOCKED
              </span>
            )}
            <br />
            <span style={{ fontSize: "12px", color: "#666" }}>
              Type: {org.type} • ID: {org.id}
              {org.createdAt &&
                ` • Created: ${org.createdAt.toDate().toLocaleDateString()}`}
            </span>
            <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                type="button"
                style={{ ...styles.button, margin: 0, fontSize: "12px" }}
                onClick={() => toggleOrgLock(org)}
                disabled={orgBusy === org.id}
              >
                {orgBusy === org.id
                  ? "Working…"
                  : org.locked
                  ? "Unlock"
                  : "Lock"}
              </button>
              <button
                type="button"
                style={{
                  ...styles.button,
                  margin: 0,
                  fontSize: "12px",
                  background: "#fee",
                  color: "#a00",
                  borderColor: "#f3c0c0",
                }}
                onClick={() => hardDelete(org)}
                disabled={orgBusy === org.id}
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </>
  );
}
